/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { AccessibilityProvider } from "@/components/accessibility/accessibility-provider";
import { AppStoreInitializer } from "@/components/providers/app-store-provider";
import { CommandPaletteProvider } from "@/components/providers/command-palette-provider";
import { FeatureFlagsProvider } from "@/components/providers/feature-flags-provider";
import { NetworkStatusProvider } from "@/components/providers/network-status-provider";
import { QueryProvider } from "@/components/providers/query-provider";
import { RateLimitProvider } from "@/components/providers/rate-limit-provider";
import { RouteAnnouncer } from "@/components/providers/route-announcer";
import { ThemeModeProvider } from "@/components/providers/theme-mode-provider";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { UserPreferencesProvider } from "@/components/providers/user-preferences-provider";
import { ErrorBoundary } from "@/components/shared/error-boundary-enhanced";
import { MaintenanceBanner } from "@/components/ui/maintenance-banner";
import { RateLimitBanner } from "@/components/ui/rate-limit-banner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthorizationProvider } from "@/components/providers/authorization-provider";
import { OrganizationProvider } from "@/contexts/organization-context";
import { OrganizationDataProvider } from "@/hooks/auth/use-organization";
import { SupabaseProvider } from "@/hooks/integrations/use-supabase";
import { getSupabaseClient } from "@/lib/supabase/client";
import { logger } from "@/lib/utils/logger";
import { isMarketingPage } from "@/lib/utils/domain/marketing-pages";
import { ClerkProvider, useOrganization } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useMemo } from "react";

// NOTE: Environment init removed from here to prevent compilation blocking
// It will be initialized lazily when needed instead of on module import
// This prevents compilation hangs in Next.js 16 canary

// NOTE: isTestModeActive() function removed Jan 2026.
// We now use REAL Clerk authentication via @clerk/testing for all tests,
// so ClerkProvider is always included (no more skipping in test mode).

/**
 * Get Clerk configuration (lazy evaluation to prevent compilation blocking).
 * publishableKey may be undefined at build time (e.g. CI without env), which would
 * break prerender of /_not-found if we always used ClerkProvider.
 */
function getClerkConfig() {
  return {
    publishableKey: process.env["NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY"],
    signInUrl: process.env["NEXT_PUBLIC_CLERK_SIGN_IN_URL"] ?? "/auth",
    signUpUrl: process.env["NEXT_PUBLIC_CLERK_SIGN_UP_URL"] ?? "/auth",
    signInFallbackRedirectUrl:
      process.env["NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_URL"] ?? "/post-auth",
    signUpFallbackRedirectUrl:
      process.env["NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_URL"] ?? "/post-auth",
  };
}

/**
 * Watches for organization switches and clears the React Query cache
 * to prevent cross-org data leakage via stale cached entries.
 *
 * Must be rendered inside both ClerkProvider (for useOrganization)
 * and QueryClientProvider (for useQueryClient).
 */
function OrgCacheGuard({ children }: { children: React.ReactNode }) {
  const { organization, isLoaded } = useOrganization();
  const queryClient = useQueryClient();
  const previousOrgId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    const currentOrgId = organization?.id ?? null;

    // Skip initial mount (no previous org to compare against)
    if (previousOrgId.current === null) {
      previousOrgId.current = currentOrgId;
      return;
    }

    // Org changed — flush all cached data to prevent cross-org leakage
    if (currentOrgId !== previousOrgId.current) {
      logger.info(
        "[ORG CACHE GUARD] Organization changed, clearing query cache",
        {
          from: previousOrgId.current,
          to: currentOrgId,
        },
      );
      queryClient.clear();
      previousOrgId.current = currentOrgId;
    }
  }, [organization?.id, isLoaded, queryClient]);

  return <>{children}</>;
}

export function RootProviders({ children }: { children: React.ReactNode }) {
  // Lazy initialize Supabase client to prevent compilation blocking
  // This defers client creation until runtime instead of during module evaluation
  const supabaseClient = React.useMemo(() => getSupabaseClient(), []);

  // Lazy evaluation of Clerk config - only accessed when component renders
  const clerkConfig = React.useMemo(() => getClerkConfig(), []);

  // PERF: Check if we're on a marketing page to avoid loading user/org providers
  // Marketing pages don't need user preferences, organization data, or app store
  const pathname = usePathname();
  const isMarketing = useMemo(
    () => isMarketingPage(pathname || "/"),
    [pathname],
  );

  // Core providers needed on ALL pages (including marketing)
  const coreProviders = (
    <QueryProvider>
      <SupabaseProvider supabase={supabaseClient}>
        <FeatureFlagsProvider>
          <RateLimitProvider>
            <NetworkStatusProvider>
              <AccessibilityProvider>
                <TooltipProvider>
                  <CommandPaletteProvider>
                    <MaintenanceBanner />
                    <RateLimitBanner />
                    <RouteAnnouncer />
                    {children}
                  </CommandPaletteProvider>
                </TooltipProvider>
              </AccessibilityProvider>
            </NetworkStatusProvider>
          </RateLimitProvider>
        </FeatureFlagsProvider>
      </SupabaseProvider>
    </QueryProvider>
  );

  // When publishableKey is missing (e.g. build-time prerender without env), skip
  // ClerkProvider so static routes like /_not-found can prerender without throwing.
  const hasClerkKey = Boolean(clerkConfig.publishableKey);

  // If marketing page, skip user/org providers to avoid unnecessary data fetching
  // These providers trigger API calls and Clerk initialization even when disabled
  if (isMarketing) {
    const content = hasClerkKey ? (
      <ClerkProvider {...clerkConfig}>{coreProviders}</ClerkProvider>
    ) : (
      coreProviders
    );
    return <ErrorBoundary>{content}</ErrorBoundary>;
  }

  // Authenticated pages get full provider stack
  const innerProviders = (
    <QueryProvider>
      <SupabaseProvider supabase={supabaseClient}>
        <ThemeModeProvider>
          <OrganizationProvider>
            <OrgCacheGuard>
              <OrganizationDataProvider>
                <AuthorizationProvider>
                  <AppStoreInitializer>
                    <UserPreferencesProvider>
                      <ThemeProvider>
                        <FeatureFlagsProvider>
                          <RateLimitProvider>
                            <NetworkStatusProvider>
                              <AccessibilityProvider>
                                <TooltipProvider>
                                  <CommandPaletteProvider>
                                    <MaintenanceBanner />
                                    <RateLimitBanner />
                                    <RouteAnnouncer />
                                    {children}
                                  </CommandPaletteProvider>
                                </TooltipProvider>
                              </AccessibilityProvider>
                            </NetworkStatusProvider>
                          </RateLimitProvider>
                        </FeatureFlagsProvider>
                      </ThemeProvider>
                    </UserPreferencesProvider>
                  </AppStoreInitializer>
                </AuthorizationProvider>
              </OrganizationDataProvider>
            </OrgCacheGuard>
          </OrganizationProvider>
        </ThemeModeProvider>
      </SupabaseProvider>
    </QueryProvider>
  );

  // When publishableKey is set, wrap with ClerkProvider. When missing (e.g. build
  // prerender of /_not-found), render core tree only so prerender can succeed.
  const content = hasClerkKey ? (
    <ClerkProvider {...clerkConfig}>{innerProviders}</ClerkProvider>
  ) : (
    coreProviders
  );
  return <ErrorBoundary>{content}</ErrorBoundary>;
}
